import { Inject, Injectable, Optional } from '@angular/core';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { HttpClient } from '@angular/common/http';
import { ApolloLink, InMemoryCache, Operation } from '@apollo/client/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { LoggerService } from 'src/app/services/logger.service';

/**
 * Service that creates and initialises the Apollo for angular client
 * Make sure you inject this service before using apollo
 */
@Injectable({
  providedIn: 'root',
})
export class ApolloRunnerService {
  constructor(
    private readonly httpLink: HttpLink,
    private readonly http: HttpClient,
    private readonly apollo: Apollo,
    private loggerService: LoggerService,
    @Inject('graphqlUrl') @Optional() private graphqlUrl?: string,
  ) {
    this.createApollo(this.graphqlUrl);
  }

  existingNamedClients: string[] = [];
  wsClient: SubscriptionClient;
  wsLink: string;

  getClient() {
    return this.apollo;
  }

  /*
   * create the apollo connection
   * */
  createApollo(wsLink: string, name: string = null) {
    if (!this.wsClient || (name && !this.existingNamedClients.includes(name))) {
      // either make default once or named client multiple times
      try {
        this.existingNamedClients.push(name);
        this.wsLink = wsLink;
        const link = this.setupLink();
        const cache = new InMemoryCache();
        if (name) {
          this.apollo.createNamed(name, { link, cache } as any);
        } else {
          this.apollo.createDefault({ link, cache } as any);
        }
      } catch (e) {
        console.error('error while creating apollo -->', e);
      }
    }
  }

  /*
   * return the merged link to create the apollo connection.
   * */
  setupLink(): ApolloLink {
    const httpLink = new HttpLink(this.http).create({
      uri: `${location.protocol}//${this.wsLink}`,
      withCredentials: true,
    });
    this.wsClient = new SubscriptionClient(
      `${/https/.test(location.protocol) ? 'wss' : 'ws'}://${this.wsLink}`,
      this.getSubscriptionConfig(),
    );
    const afterwareLink = this.setupAfterwareLink();

    this.wsClient.onConnected(() => this.loggerService.log('WS onConnected'));
    this.wsClient.onReconnected(() =>
      this.loggerService.log('WS onReconnected'),
    );
    this.wsClient.onConnecting(() => this.loggerService.log('WS onConnecting'));
    this.wsClient.onReconnecting(() =>
      this.loggerService.log('WS onReconnecting'),
    );
    this.wsClient.onDisconnected(() =>
      this.loggerService.log('WS onDisconnected'),
    );
    const wsLink = new WebSocketLink(this.wsClient);
    wsLink['subscriptionClient'].maxConnectTimeGenerator.duration = () =>
      wsLink['subscriptionClient'].maxConnectTimeGenerator.max;
    return ApolloLink.split(
      this.isSubscription,
      wsLink,
      afterwareLink.concat(httpLink as any),
    );
  }

  /* istanbul ignore next */
  private setupAfterwareLink(): ApolloLink {
    return new ApolloLink((operation: Operation, forward: any) => {
      if (!forward) {
        return null;
      }
      const forwaded = forward(operation);
      if (!forwaded || !forwaded.map) {
        return forwaded;
      }
      return forwaded.map((response: any) => {
        const context = operation.getContext();
        const {
          response: { headers },
        }: any = context;
        if (headers) {
          const jwt = headers.get('x-application-authorization-cart');
          this.loggerService.log('Headers : ' + JSON.stringify(headers));
          this.loggerService.log(
            'Dans le setupAfterwareLink avec JWT : ' + jwt,
          );
          if (!response.context) {
            response.context = {};
          }
          response.context.jwt = jwt;
        }
        return response;
      });
    });
  }

  /*
   * close the existing/unused websocket.
   * */
  closeWebSocket() {
    if (this.wsClient) {
      this.wsClient.close();
    }
  }

  /*
   * config options for ws.
   * */
  getSubscriptionConfig() {
    return {
      reconnect: true,
      lazy: true,
    };
  }

  isSubscription(operation: Operation): boolean {
    const opDef: any = getMainDefinition(operation.query);
    return (
      opDef.kind === 'OperationDefinition' && opDef.operation === 'subscription'
    );
  }
}
